import React, {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';

import { FlatList, ListRenderItemInfo, View } from 'react-native';

import Carousel from 'react-native-reanimated-carousel';
import {
    CarouselRenderItemInfo,
    ICarouselInstance,
} from 'react-native-reanimated-carousel/lib/typescript/types';
import { useDispatch, useSelector } from 'react-redux';

import { EPGChannel } from 'custom_modules/react-native-epg';

import EpgChannelListDesktopSkeletonLoading from 'assets/images/animation/epg_channel_list_web.svg';

import { selectLayoutConfig } from 'store/config/selectors';
import { setPreviouslyWatchedChannel } from 'store/epg/actions';
import { selectPreviouslyWatchedChannel } from 'store/epg/selectors';
import { selectChannelsListWithTvChannel } from 'store/programs/selectors';
import {
    selectActiveUserProfile,
    selectEntitlements,
} from 'store/user/selectors';

import { ContentType } from 'services/data-manager/types';

import {
    heightPercentageToDP,
    widthPercentageToDP,
} from 'utils/responsiveness';

import useChannels from 'screens/Epg/hooks/useChannels';
import { EpgChannelExtra, EpgFilterCategory } from 'screens/Epg/types';

import ArrowButton, { EArrowType } from 'components/CustomButtons/ArrowButton';
import SkeletonLoading from 'components/SkeletonLoading';

import { IChannelList } from '../EpgListContent/types';
import ChannelItem from './ChannelItem';
import { SIZES as CHANNEL_SIZES, SIZES } from './ChannelItem/styles';
import styles from './styles';

type Props = {
    selectedFilterCategory?: EpgFilterCategory;
};

const INFINITE_LIST_LIMIT = 20;
const ARROW_WIDTH = 56;
const ListHeaderComponent = () => (
    <View accessible={false} style={styles.finiteListHeaderFooter} />
);
const ItemSeparatorComponent = () => (
    <View style={styles.finiteListSeparator} />
);

const EpgChannelList = forwardRef<IChannelList, Props>(
    ({ selectedFilterCategory }) => {
        const channelsList = useSelector(selectChannelsListWithTvChannel);
        const entitlements = useSelector(selectEntitlements);
        const previouslyWatchedChannels = useSelector(
            selectPreviouslyWatchedChannel,
        );
        const selectedProfileId = useSelector(selectActiveUserProfile);
        const { background } = useSelector(selectLayoutConfig);

        const dispatch = useDispatch();

        const carouselRef = useRef<ICarouselInstance>(null);
        const listRef = useRef<FlatList<EPGChannel<EpgChannelExtra>>>(null);

        const channels = useChannels({
            channels: channelsList,
            filterCategory: selectedFilterCategory,
            entitlements,
        });

        const previouslyWatchedChannel = useMemo(
            () =>
                selectedProfileId
                    ? previouslyWatchedChannels[selectedProfileId]
                    : undefined,
            [selectedProfileId, previouslyWatchedChannels],
        );

        const selectedChannel = useMemo(
            () =>
                channels.find(({ id }) => previouslyWatchedChannel === id) ??
                channels[0],
            [channels, previouslyWatchedChannel],
        );
        const selectedChannelIndex = useMemo(() => {
            const index = channels.findIndex(
                ({ id }) => previouslyWatchedChannel === id,
            );

            if (previouslyWatchedChannel && index >= 0) {
                return index;
            }

            return 0;
        }, [channels, previouslyWatchedChannel]);

        const onChannelSelected = useCallback(
            (channel: EPGChannel<EpgChannelExtra>) => {
                dispatch(
                    setPreviouslyWatchedChannel({
                        channelId: channel.id,
                        profileId: selectedProfileId,
                    }),
                );

                const index = channels.findIndex(({ id }) => channel.id === id);

                carouselRef.current?.scrollTo({
                    animated: false,
                    index: index >= 0 ? index : 0,
                });
            },
            [dispatch, selectedProfileId, channels],
        );

        const renderItem = useCallback(
            ({
                item,
                index,
            }: CarouselRenderItemInfo<EPGChannel<EpgChannelExtra>>) => (
                <ChannelItem
                    key={item.id}
                    channel={item}
                    index={index}
                    onPress={onChannelSelected}
                    selected={item.id === selectedChannel?.id}
                    totalNumberOfItems={channels.length}
                />
            ),
            [selectedChannel, channels.length, onChannelSelected],
        );

        const renderListItem = useCallback(
            ({
                item,
                index,
            }: ListRenderItemInfo<EPGChannel<EpgChannelExtra>>) => (
                <ChannelItem
                    key={item.id}
                    channel={item}
                    index={index}
                    onPress={onChannelSelected}
                    selected={item.id === selectedChannel?.id}
                    totalNumberOfItems={channels.length}
                    style={styles.finiteListChannel}
                    styleSelected={styles.finiteListChannelSelected}
                />
            ),
            [selectedChannel, channels.length, onChannelSelected],
        );

        const getListItemLayout = useCallback(
            (_, index) => ({
                length: CHANNEL_SIZES.width,
                offset: (CHANNEL_SIZES.width + CHANNEL_SIZES.spacing) * index,
                index,
            }),
            [],
        );

        const handleArrowPress = useCallback(
            (type: EArrowType) => {
                const count = type === EArrowType.BWD ? -1 : 1;
                let channelId: string | undefined;

                if (carouselRef.current) {
                    carouselRef.current.scrollTo({
                        animated: true,
                        count,
                    });
                    channelId = channels.slice(
                        carouselRef.current?.getCurrentIndex(),
                    )[0]?.id;
                }

                if (
                    listRef.current &&
                    selectedChannelIndex + count >= 0 &&
                    selectedChannelIndex + count < channels.length
                ) {
                    channelId = channels[selectedChannelIndex + count]?.id;
                }

                if (channelId) {
                    dispatch(
                        setPreviouslyWatchedChannel({
                            channelId,
                            profileId: selectedProfileId,
                        }),
                    );
                }
            },
            [selectedChannelIndex, channels, dispatch, selectedProfileId],
        );

        const handleScrollEnd = useCallback(
            (index: number) => {
                dispatch(
                    setPreviouslyWatchedChannel({
                        channelId: channels[index].id,
                        profileId: selectedProfileId,
                    }),
                );
            },
            [channels, dispatch, selectedProfileId],
        );

        useEffect(() => {
            if (carouselRef.current) {
                setTimeout(
                    () =>
                        carouselRef.current?.scrollTo({
                            animated: false,
                            index: selectedChannelIndex,
                        }),
                    100,
                );
            }

            if (listRef.current) {
                setTimeout(() => {
                    listRef.current?.scrollToIndex({
                        animated: true,
                        index: selectedChannelIndex,
                        viewPosition: 0.5,
                        viewOffset:
                            // eslint-disable-next-line no-nested-ternary
                            selectedChannelIndex === 0
                                ? ARROW_WIDTH
                                : selectedChannelIndex === channels.length - 1
                                ? -ARROW_WIDTH
                                : -SIZES.width / 2,
                    });
                    dispatch(
                        setPreviouslyWatchedChannel({
                            channelId: channels[selectedChannelIndex].id,
                            profileId: selectedProfileId,
                        }),
                    );
                }, 100);
            }
        }, [selectedFilterCategory, selectedChannelIndex]);

        return (
            <View>
                {channels.length > 1 && (
                    <ArrowButton
                        type={EArrowType.BWD}
                        onArrowNavigationPress={handleArrowPress}
                        hasTitle={false}
                        width={ARROW_WIDTH}
                        metadataType={ContentType.ChannelItem}
                        style={[styles.arrow, styles.arrowBwd]}
                        tabIndex={0}
                    />
                )}
                {!channels?.length && (
                    <SkeletonLoading
                        isScreenSkeleton={false}
                        SkeletonLoadingComponent={
                            EpgChannelListDesktopSkeletonLoading
                        }
                        backgroundColor={background}
                        width={widthPercentageToDP(1280)}
                        height={heightPercentageToDP(156)}
                    />
                )}
                {channels.length && channels.length > INFINITE_LIST_LIMIT && (
                    <Carousel
                        ref={carouselRef}
                        data={channels}
                        defaultIndex={selectedChannelIndex}
                        width={SIZES.width + (SIZES.spacing + 2) * 2}
                        height={SIZES.heightSelected}
                        renderItem={renderItem}
                        style={styles.carouselContainer}
                        onScrollEnd={handleScrollEnd}
                        loop
                    />
                )}
                {channels.length && channels.length <= INFINITE_LIST_LIMIT && (
                    <FlatList
                        // @ts-ignore
                        ref={listRef}
                        data={channels}
                        initialScrollIndex={selectedChannelIndex}
                        renderItem={renderListItem}
                        getItemLayout={getListItemLayout}
                        style={styles.webList}
                        contentContainerStyle={styles.webListContainer}
                        showsHorizontalScrollIndicator={false}
                        scrollEnabled={false}
                        ItemSeparatorComponent={ItemSeparatorComponent}
                        ListHeaderComponent={ListHeaderComponent}
                        ListFooterComponent={ListHeaderComponent}
                        horizontal
                    />
                )}
                {channels.length > 1 && (
                    <ArrowButton
                        type={EArrowType.FWD}
                        onArrowNavigationPress={handleArrowPress}
                        hasTitle={false}
                        width={ARROW_WIDTH}
                        metadataType={ContentType.ChannelItem}
                        style={[styles.arrow, styles.arrowFwd]}
                        tabIndex={0}
                    />
                )}
            </View>
        );
    },
);

export default EpgChannelList;
